import React from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import NavBar from './NavBar';
import NoAccess from './NoAccess';
import { usePermissions } from '../hooks/usePermissions';
import { useRoutePermission, findRouteForPathname } from '../hooks/useRoutePermission';
import { PERMISSIONS } from '../config/permissions';

/**
 * AppLayout serves as the main wrapper for all routes and page views.
 * It includes:
 *  - User menu for authenticated users (sign out, profile).
 *  - A floating action button to add group data (only if user has permission).
 *  - Primary navigation: bottom bar on a phone, top bar on a wide screen.
 *  - An <Outlet /> to display the content of the currently selected route.
 *  - Route-level permission check (defense in depth). Users missing the module
 *    key see a NoAccess notice inside the layout rather than a silent redirect,
 *    so they can tell an administrator which key they need.
 */
const AppLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hasPermission } = usePermissions();
  const { allowed, requiredPermission } = useRoutePermission();

  return (
    <div className="app-layout">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header className="app-header">
        <Link to="/" className="app-wordmark">Ministry Data Portal</Link>
        <NavBar />
        <div className="app-header-user">
          <UserButton />
        </div>
      </header>
      <main id="main-content">
        {allowed ? (
          <Outlet />
        ) : (
          <NoAccess
            moduleLabel={findRouteForPathname(pathname)?.label}
            requiredPermission={requiredPermission}
          />
        )}
      </main>

      {hasPermission(PERMISSIONS.GROUP_DATA) && (
      <button
        className="fab-button"
        onClick={() => navigate('/dynamic-entry')}
        aria-label="Add Group Data"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        <span className="fab-label">Add Group Data</span>
      </button>
      )}

    </div>
  );
};

export default AppLayout;